import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const ThemeContext = createContext({ theme: 'light', toggle: () => {} });

const LIGHT = 'light';
const DARK  = 'dark';

// Keep a browser fallback per account so failed saves and unrelated auth
// notifications cannot undo the selected theme.
function themeStorageKey() {
  try {
    const user = JSON.parse(localStorage.getItem('adeeb-user') || 'null');
    return 'adeeb-theme:' + (user?._id || 'guest');
  } catch { return 'adeeb-theme:guest'; }
}

function readUserTheme() {
  if (typeof window === 'undefined') return LIGHT;
  try {
    const saved = localStorage.getItem(themeStorageKey());
    if (saved === LIGHT || saved === DARK) return saved;
    const user = JSON.parse(localStorage.getItem('adeeb-user') || 'null');
    return user?.theme === DARK ? DARK : LIGHT;
  } catch { return LIGHT; }
}

function applyThemeToDocument(theme) {
  const root = document.documentElement;
  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(`theme-${theme}`);
  root.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readUserTheme);
  // Tracks whether a server PATCH is in flight; pre-empts overlapping
  // toggles so a fast double-click doesn't desync local + server.
  const inFlight = useRef(false);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // React to login/logout/profile-change in other tabs.
  useEffect(() => {
    const sync = (event) => {
      if (event.type === 'storage' && event.key !== null &&
          !['adeeb-user', 'adeeb-token', themeStorageKey()].includes(event.key)) return;
      setTheme(readUserTheme());
    };
    window.addEventListener('storage', sync);
    window.addEventListener('adeeb-auth-change', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('adeeb-auth-change', sync);
    };
  }, []);

  const toggle = useCallback(async () => {
    if (inFlight.current) return;
    const next = theme === DARK ? LIGHT : DARK;
    try { localStorage.setItem(themeStorageKey(), next); } catch { /* storage unavailable */ }
    setTheme(next);
    applyThemeToDocument(next);

    const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
    if (!token) return;

    inFlight.current = true;
    try {
      const { data } = await axios.patch(
        '/api/auth/me/theme',
        { theme: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Keep the cached user in sync so a reload doesn't snap back.
      if (data?.user && localStorage.getItem('adeeb-token') === token) {
        try {
          const cached = JSON.parse(localStorage.getItem('adeeb-user') || 'null') || {};
          localStorage.setItem('adeeb-user', JSON.stringify({ ...cached, ...data.user }));
          window.dispatchEvent(new Event('adeeb-auth-change'));
        } catch { /* ignore */ }
      }
    } catch {
      // Keep the browser preference when offline or the session has expired.
      // The next toggle will attempt to save to the account again.
    } finally {
      inFlight.current = false;
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeToggle({ className = '' }) {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      className={`theme-toggle ${className}`}
      onClick={toggle}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}
